import type { UpdateTrailerInput } from '@chukua/common';
import { Alert, Button, Form, Input, InputNumber, Select, Switch } from 'antd';
import { useEffect } from 'react';
import type { TrailerView } from '../trailers.api';
import { useTrailerTypeOptions } from '../hooks/use-trailer-type-options';
import { VehicleColorField } from '../../../components/VehicleColorField';
import { CAPACITY_TONNES_RULES } from '../../../../../shared/policies/capacity-tonnes-rules';

interface TrailerDetailsFormProps {
  trailer: TrailerView;
  canManage: boolean;
  error: string | null;
  saving: boolean;
  onSave: (input: UpdateTrailerInput) => Promise<boolean>;
}

function toFormValues(trailer: TrailerView): UpdateTrailerInput {
  return {
    registration: trailer.registration,
    typeCode: trailer.typeCode,
    capacityTonnes: trailer.capacityTonnes,
    hasGenset: trailer.hasGenset,
    color: trailer.color,
  };
}

/** The trailer's fields, editable inline for `truck.manage` holders — read-only for everyone else with `fleet.view` (CLAUDE.md: nav-hiding and disabled controls are UX only, the server remains the authority). */
export function TrailerDetailsForm({ trailer, canManage, error, saving, onSave }: TrailerDetailsFormProps) {
  const [form] = Form.useForm<UpdateTrailerInput>();
  const typeOptions = useTrailerTypeOptions();
  const options = typeOptions.some((type) => type.code === trailer.typeCode)
    ? typeOptions
    : [...typeOptions, { id: trailer.typeCode, code: trailer.typeCode, name: trailer.typeCode, isActive: false }];

  useEffect(() => {
    form.setFieldsValue(toFormValues(trailer));
  }, [trailer, form]);

  async function submit(values: UpdateTrailerInput) {
    await onSave(values);
  }

  return (
    <Form<UpdateTrailerInput>
      form={form}
      layout="vertical"
      requiredMark={false}
      onFinish={submit}
      disabled={!canManage || saving}
      initialValues={toFormValues(trailer)}
    >
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form.Item name="registration" label="Registration" rules={[{ required: true, message: 'Enter a registration' }]}>
        <Input className="font-mono" />
      </Form.Item>
      <Form.Item name="typeCode" label="Trailer type" rules={[{ required: true, message: 'Select a trailer type' }]}>
        <Select options={options.map((type) => ({ value: type.code, label: type.name }))} showSearch optionFilterProp="label" />
      </Form.Item>
      <Form.Item name="capacityTonnes" label="Capacity (tonnes)" rules={CAPACITY_TONNES_RULES}>
        <InputNumber className="w-full" min={0.01} />
      </Form.Item>
      <VehicleColorField />
      <Form.Item
        name="hasGenset"
        label="Genset for a reefer container"
        valuePropName="checked"
        extra="An underslung or clip-on set on this trailer. What the trailer is, and its axles, come from its type."
      >
        <Switch />
      </Form.Item>
      {canManage && (
        <Form.Item className="mb-0">
          <Button type="primary" htmlType="submit" loading={saving}>
            Save changes
          </Button>
        </Form.Item>
      )}
    </Form>
  );
}
